// src/services/pftMovePlan.js
//
// Moving a PFT Uploads card into Lung Center Referrals. Firebase-free and tested
// (pftMovePlan.test.js); the reads and writes are in pftMoveService.js.
//
//   * findCardMatches: is this patient already in the system? An exact 9-digit MRN
//     match, or the same name + date of birth (the portal's tested matcher). A match
//     means "merge" (pftMergePlan.js); no match means "create".
//   * newPatientRecord: the new patient, in the same shape as Raw Database's "Move to
//     Referrals" (empty fields for everything the staff fill in later), plus the PFT
//     dates, sex and smoking status from the card.

import { findPotentialDuplicates, mrnOneDigitApart, sameMrnNumber } from "./patientDuplicateMatch";
import { confidenceFromNameMatch } from "./enrollmentMatch";
import { PFT_FLAT_KEYS, pftFieldsForSave, testsFromRecord } from "./pftTests";
import { collapseSameReports, importToTest } from "./pftImportGroups";
import { activeEnrolledStudies } from "./enrollmentRules";
import { statusLabel } from "../constants/diseaseOptions";
import { surveyFieldsForNewPatient } from "./pftSurveyAnswers";

export const REFERRALS_STATUS = "Lung Center Referrals";

const STORE_LABEL = { consents: "Referrals / patient list", duplicates: "Duplicates", sheet: "Google Sheet (Dubois)" };

/**
 * Patients already in the system that may be this card's patient.
 *   candidates: [{ id, store: "consents" | "sheet", ...record }]. A `duplicates` record
 *   (set aside as a copy of another patient) is never a match (decided 2026-10-20).
 * Returns [{ record, store, storeLabel, how: "MRN" | "name and DOB", confidence }],
 * MRN matches first. Several matches are possible: the person picks one.
 */
// The shared rule lives in patientDuplicateMatch.js; re-exported for existing callers.
export { mrnOneDigitApart };

export const findCardMatches = (card, allCandidates = []) => {
  const candidates = allCandidates.filter((c) => c.store !== "duplicates");
  const out = [];
  const seen = new Set();
  const key = (c) => `${c.store}:${c.id}`;
  if (card.mrn9) {
    for (const c of candidates) {
      if (sameMrnNumber(c.mrn, card.mrn9) && !seen.has(key(c))) {
        seen.add(key(c));
        out.push({ record: c, store: c.store, storeLabel: STORE_LABEL[c.store] || c.store, how: "MRN", confidence: 100 });
      }
    }
  }
  // everyRecord: two copies of one patient in the system (same MRN) are both listed, so
  // staff pick which one gets the PFT data.
  const named = findPotentialDuplicates(
    { name: card.name, dob: card.dob, mrn: card.mrn9 },
    candidates.map((c) => ({ ...c, name: c.name || c.fullName || "" })),
    { everyRecord: true },
  );
  for (const m of named) {
    const c = candidates.find((x) => x.store === m.patient.store && x.id === m.patient.id) || m.patient;
    if (seen.has(key(c))) continue;
    seen.add(key(c));
    out.push({
      record: c,
      store: c.store,
      storeLabel: STORE_LABEL[c.store] || c.store,
      how: m.nameMatch ? "name and DOB" : "nearly the same MRN and the same DOB",
      confidence: m.nameMatch ? confidenceFromNameMatch(m.nameMatch) : null,
      ...(m.mrnOneOff ? { detail: `MRN ${c.mrn} vs ${card.mrn9}: one digit different` } : {}),
      reasons: m.reasons,
    });
  }
  return out;
};

// ---- How a possible match is shown (Move pop-up and Move all review) ---------------
// Short on purpose: staff skim long text and click OK.

/** One short line on why this record may be the card's patient. */
export const matchReason = (m) => {
  if (!m) return "";
  if (m.how === "MRN") return "Same MRN";
  if (!m.confidence) return "Same DOB + MRN one digit off";
  const first = (m.reasons || [])[0];
  return m.confidence < 100 && first ? `Same DOB + name · ${first}` : "Same DOB + name";
};

/** The box (status) the record sits in, as staff see it. */
export const matchBox = (m) => {
  if (!m) return "";
  if (m.store === "duplicates") return "Potential Duplicate"; // as the patient page shows it
  return statusLabel((m.record || {}).status) || "";
};

/**
 * The studies the patient is currently in (not closed, dismissed or rejected):
 * [{ name, approved }]. approved = an admin approved it onto the study list.
 */
export const currentStudies = (record) =>
  activeEnrolledStudies((record || {}).enrolledStudies)
    .filter((e) => !e.closedAt && (e.studyName || e.studyId))
    .map((e) => ({ name: e.studyName || e.studyId, approved: e.approval === "approved" }));

/** Link to the record's patient page (Sheet patients by token). */
export const patientHref = (m) =>
  `/coordinator/patient/${encodeURIComponent((m.record || {}).id)}?src=${m.store === "sheet" ? "sheet" : "firestore"}`;

// Same empty clinical fields a Raw Database move writes (RawDatabasePage EMPTY_CLINICAL),
// minus the PFT ones, which come from the card.
const EMPTY_CLINICAL = {
  eosNA: false, eosValues: [], exacNA: false, exacerbations: [], ageNA: false, age: "",
  pftNA: false,
  methNA: false, methResult: "", methDate: "", fenoNA: false, fenoPPB: "", fenoDate: "",
  oxygenNA: false, oxygenLitres: "", oxygenType: "PRN", cancerNA: false, cancerStatus: "", cancerDate: "",
  bmi: "", ahi: "", a1c: "", a1cDate: "", diabetes: "",
};

/**
 * The new patient record for a card with no match (no timestamps: the service adds
 * them). diagnosis: the optional diagnosis picked at move time. survey: the patient's
 * health-survey answers (pft_survey_submissions), when they answered: contact details,
 * diagnosis, consent and notes from it (pftSurveyAnswers.surveyFieldsForNewPatient).
 * Without a survey the record is exactly as before.
 */
export const newPatientRecord = (card, { diagnosis = [], actor = "unknown", survey = null } = {}) => {
  const record = basePatientRecord(card, { diagnosis, actor });
  return survey ? { ...record, ...surveyFieldsForNewPatient(record, survey, { by: actor }) } : record;
};

const basePatientRecord = (card, { diagnosis, actor }) => {
  // A report found in several PDFs is added once (it remembers every PDF in alsoIn).
  const tests = collapseSameReports((card.pendingImports || []).map(importToTest));
  return {
    name: card.name || "",
    dob: card.dob || "",
    phone: card.phone || "",
    email: "",
    physicalAddress: card.address || "",
    diagnosis,
    status: REFERRALS_STATUS,
    notes: "",
    consentUrl: "",
    mrn: card.mrn9 || "",
    study: "",
    potentialStudyIds: [],
    studyEligibility: {},
    emergencyContactName: "",
    emergencyContactPhone: "",
    callMade: false,
    scheduled: false,
    dateScheduled: "",
    rescreenDate: "",
    urgent: false,
    urgentReason: "",
    ...EMPTY_CLINICAL,
    // The PFT dates; the flat PFT fields mirror the newest one (pftTests.js).
    ...pftFieldsForSave({ stored: {}, base: [], local: tests }),
    pftTests: tests,
    ...(card.sex ? { sex: card.sex } : {}),
    ...(card.smokingStatus ? { smokingStatus: card.smokingStatus } : {}),
    ...(card.smokingNote ? { smokingNote: card.smokingNote } : {}),
    // provenance
    source: "PftUpload",
    pftImportIds: (card.pendingImports || []).map((i) => i.id),
    movedToReferralsBy: actor,
  };
};

/** A short fingerprint of a merge plan's choices, to notice the patient changed meanwhile. */
export const planSignature = (plan) =>
  JSON.stringify({
    p: plan.profile.filter((r) => r.kind === "conflict" || r.kind === "fill").map((r) => [r.field, r.current, r.incoming]),
    t: plan.tests.map((t) => [t.incoming.id, t.kind, (t.fields || []).filter((f) => f.kind !== "same" && f.kind !== "keep").map((f) => [f.field, f.current])]),
  });

// ---- Google Sheet patients -------------------------------------------------------
// The request for functions' mergePftIntoSheetPatient (rule: functions/sheetPftMerge.js),
// built from the Sheet row as read and the applied merge. Tested against the server
// rule itself in pftSheetMerge.test.js.
const str = (v) => (v === undefined || v === null ? "" : String(v).trim());

/** What to send for a Sheet merge: field changes (with what was seen) and the PFT list. */
export const sheetMergeBody = (record, result, survey = null) => ({
  ...sheetMergeBodyBase(record, result),
  // A patient who answered the health survey: what to ADD (pftSurveyAnswers.sheetSurveyAdditions).
  ...(survey ? { survey } : {}),
});

const sheetMergeBodyBase = (record, result) => {
  const changes = Object.entries(result.profileUpdates).map(([field, to]) => ({
    field,
    from: field === "name" ? record.fullName ?? record.name : record[field],
    to,
  }));
  const pft = pftFieldsForSave({ stored: record, base: testsFromRecord(record), local: result.tests });
  for (const k of PFT_FLAT_KEYS) {
    if (str(pft[k]) !== str(record[k])) changes.push({ field: k, from: record[k] ?? "", to: pft[k] });
  }
  return {
    changes,
    ...(pft.pftTests ? { pftTests: pft.pftTests } : {}),
    expectTestIds: (Array.isArray(record.pftTests) ? record.pftTests : []).filter((t) => t && t.id).map((t) => t.id),
  };
};

